/**
 * What gets shared, and the one URL it points at.
 *
 * Pure on purpose, like policy-text.js and job-title.js: the microsite has no
 * Capacitor dependency, so anything in shared/ui that reached for a native
 * plugin would break its build. Each surface keeps its own thin component over
 * this — the same split AddToCalendar already uses over shared/calendar.
 *
 * The caller passes an already-translated message; no i18n lives here.
 */

/**
 * Where a share points.
 *
 * A constant rather than `window.location.origin`, because neither runtime
 * gives the right answer: under Capacitor the app is served from
 * capacitor://localhost, and in dev the microsite is localhost:5174. Both would
 * share a link that resolves to nothing on the recipient's phone.
 */
export const SHARE_URL = 'https://themondayfestival.gr/';

export const SHARE_TITLE = 'The Monday Festival';

/** Collapse the newlines and double spaces that creep into translated copy. */
const tidy = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

/**
 * The payload for a native share sheet.
 *
 * `url` stays in its own field rather than being glued onto the text: that is
 * what lets the receiving app resolve the link preview, which is the whole
 * point of the card.
 */
export function sharePayload({ message, url = SHARE_URL, title = SHARE_TITLE } = {}) {
  return { title, text: tidy(message), url };
}

/**
 * One string, for the clipboard and for any target that ignores `url`.
 *
 * The link is appended only when the message does not already carry it. Android's
 * share sheet concatenates `text` and `url` itself, so a message that already
 * ends in the URL arrives with the link in it twice — which looks like a bug to
 * everyone who receives it.
 */
export function shareFallbackText({ message, url = SHARE_URL } = {}) {
  const text = tidy(message);
  if (!text) return url;

  /*
   * Match on the host, not the whole URL.
   *
   * Copy names the site the way a person would — "themondayfestival.gr — δες
   * το" — with no scheme and no trailing slash. Comparing against the full
   * https:// URL misses that and appends the link anyway, so the message
   * arrives naming the site twice. Which is precisely what a recipient
   * notices.
   */
  let host;
  try {
    host = new URL(url).host;
  } catch {
    host = String(url).replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  }

  return text.toLowerCase().includes(host.toLowerCase()) ? text : `${text} ${url}`;
}
